/**
 * Postmoro Affiliate Program — public marketing + application page.
 *
 * Route: /affiliate-program
 *
 * Unauthenticated visitors see the "Apply to Become an Affiliate" CTA
 * pointing to /login (with return-to). Authenticated users see the
 * inline application form; already-approved affiliates see a "Go to
 * your dashboard" prompt.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
const FEATURES = [
  "50% commission",
  "12 months of recurring commission",
  "60-day referral window",
  "Monthly payouts",
  "Track clicks, signups, customers, and earnings",
];

export default function AffiliateProgramPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    website: "",
    audience: "",
    promo_plan: "",
    audience_size: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api
      .get("/affiliate/me")
      .then((r) => setMe(r.data))
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, [user?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login?next=/affiliate-program");
      return;
    }
    // Session-locked identity confirmation — force the user to
    // acknowledge WHICH Postmoro account will become the affiliate.
    // The backend uses the session's user_id/email/name and cannot
    // be overridden by any form field; this dialog just makes that
    // binding visible so a wrong-account submit is impossible to miss.
    const confirmed = window.confirm(
      `You're applying as:\n\n` +
      `${user?.name || "(no name)"}\n${user?.email || "(no email)"}\n\n` +
      `Continue? If this isn't the account you want to enroll, cancel ` +
      `and sign out first.`
    );
    if (!confirmed) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        audience_size: form.audience_size ? parseInt(form.audience_size, 10) : null,
      };
      const r = await api.post("/affiliate/apply", payload);
      const appliedEmail = r?.data?.applied_as?.email || user?.email || "";
      if (r?.data?.already_applied) {
        toast.info(`You've already applied as ${appliedEmail}.`);
      } else {
        toast.success(`Application submitted as ${appliedEmail}.`);
      }
      const meResp = await api.get("/affiliate/me");
      setMe(meResp.data);
    } catch (err) {
      toast.error("Could not submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-0)]" data-testid="affiliate-program-page">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <p className="uppercase tracking-widest text-xs text-[color:var(--text-2)] mb-2">
          Postmoro Affiliate Program
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
          Earn 50% with Postmoro
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--text-1)] max-w-2xl">
          Refer businesses to Postmoro and earn 50% recurring commission on
          qualifying subscription revenue for their first 12 months.
        </p>

        <ul className="mt-8 grid gap-2 sm:grid-cols-2 max-w-2xl">
          {FEATURES.map((f) => (
            <li
              key={f}
              className="flex items-center gap-2 text-sm text-[color:var(--text-1)]"
              data-testid="affiliate-feature"
            >
              <span className="text-[color:var(--accent)]">•</span>
              {f}
            </li>
          ))}
        </ul>

        <div className="mt-12 rounded-lg border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6 sm:p-8">
          {!user ? (
            <div>
              <h2 className="font-display text-2xl mb-2">Apply to become an affiliate</h2>
              <p className="text-sm text-[color:var(--text-1)] mb-4">
                Sign in first — your application is tied to your Postmoro account.
              </p>
              <Button
                onClick={() => navigate("/login?next=/affiliate-program")}
                data-testid="affiliate-apply-login-btn"
              >
                Sign in to apply
              </Button>
            </div>
          ) : loading ? (
            <p className="text-sm text-[color:var(--text-2)]">Loading…</p>
          ) : me?.is_affiliate && me?.status === "active" ? (
            <div data-testid="affiliate-status-active">
              <h2 className="font-display text-2xl mb-2">You're an active affiliate</h2>
              <p className="text-sm text-[color:var(--text-1)] mb-4">
                Your referral code is <code>{me.affiliate_code}</code>.
              </p>
              <Button
                onClick={() => navigate("/affiliate")}
                data-testid="affiliate-go-dashboard-btn"
              >
                Go to your dashboard
              </Button>
            </div>
          ) : me?.is_affiliate && me?.status === "pending" ? (
            <div data-testid="affiliate-status-pending">
              <h2 className="font-display text-2xl mb-2">Application received</h2>
              <p className="text-sm text-[color:var(--text-1)]">
                We're reviewing your application. You'll get an email when it's approved.
              </p>
            </div>
          ) : me?.is_affiliate && (me?.status === "suspended" || me?.status === "disabled") ? (
            <div data-testid="affiliate-status-inactive">
              <h2 className="font-display text-2xl mb-2">Affiliate account not active</h2>
              <p className="text-sm text-[color:var(--text-1)]">
                Contact support if you believe this is a mistake.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="affiliate-apply-form">
              <h2 className="font-display text-2xl mb-2">Apply to become an affiliate</h2>
              <div
                className="rounded-md border border-[color:var(--border)] bg-[color:var(--bg-2)] px-4 py-3 text-sm"
                data-testid="affiliate-apply-identity-banner"
              >
                <p className="text-xs uppercase tracking-widest text-[color:var(--text-2)] mb-1">
                  Applying as
                </p>
                <p className="text-[color:var(--text-0)]">
                  <span className="font-medium" data-testid="affiliate-apply-identity-name">
                    {user?.name || "(no name)"}
                  </span>
                  {" · "}
                  <span data-testid="affiliate-apply-identity-email">{user?.email || "(no email)"}</span>
                </p>
                <p className="text-xs text-[color:var(--text-2)] mt-2">
                  This Postmoro account will become the affiliate.{" "}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await logout?.();
                      } finally {
                        navigate("/login?next=/affiliate-program", { replace: true });
                      }
                    }}
                    className="underline"
                    data-testid="affiliate-apply-switch-account-btn"
                  >
                    Not you? Sign out and use another account
                  </button>
                  .
                </p>
              </div>
              <div>
                <Label htmlFor="af-website">Website / social profile (optional)</Label>
                <Input
                  id="af-website"
                  value={form.website}
                  onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                  data-testid="affiliate-apply-website"
                />
              </div>
              <div>
                <Label htmlFor="af-audience">Audience / platform</Label>
                <Input
                  id="af-audience"
                  placeholder="e.g. Instagram, YouTube, newsletter"
                  value={form.audience}
                  onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
                  data-testid="affiliate-apply-audience"
                />
              </div>
              <div>
                <Label htmlFor="af-plan">How will you promote Postmoro?</Label>
                <Textarea
                  id="af-plan"
                  rows={4}
                  value={form.promo_plan}
                  onChange={(e) => setForm((f) => ({ ...f, promo_plan: e.target.value }))}
                  data-testid="affiliate-apply-plan"
                />
              </div>
              <div>
                <Label htmlFor="af-size">Estimated audience size (optional)</Label>
                <Input
                  id="af-size"
                  type="number"
                  min="0"
                  value={form.audience_size}
                  onChange={(e) => setForm((f) => ({ ...f, audience_size: e.target.value }))}
                  data-testid="affiliate-apply-size"
                />
              </div>
              <Button type="submit" disabled={submitting} data-testid="affiliate-apply-submit">
                {submitting ? "Submitting…" : "Apply to Become an Affiliate"}
              </Button>
            </form>
          )}
        </div>

        <div className="mt-12 max-w-2xl">
          <h3 className="font-display text-xl mb-2">Program terms — the short version</h3>
          <ul className="text-sm text-[color:var(--text-1)] space-y-2">
            <li>• 50% qualifying commission on collected subscription revenue.</li>
            <li>• First 12 months of the referred customer's paid subscription.</li>
            <li>• 60-day referral attribution window from first click.</li>
            <li>• 30-day hold on new commissions.</li>
            <li>• $50 minimum payout; below-threshold balances roll forward.</li>
            <li>• Monthly manual payout schedule.</li>
            <li>• Refunds and chargebacks reverse commission.</li>
            <li>• Self-referrals prohibited; fraudulent activity prohibited.</li>
            <li>• Postmoro may suspend or terminate the affiliate relationship.</li>
            <li>• Affiliates must follow applicable advertising / disclosure rules and are responsible for their own taxes.</li>
          </ul>
          <p className="text-xs text-[color:var(--text-2)] mt-4">
            <Link to="/terms" className="underline">Full terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
